import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import SocialIcons from "@/components/SocialIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-forest text-bone relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Link to="/" className="font-display text-3xl font-semibold leading-tight block">
            Seven Pillars <span className="text-terracotta">of Life</span>
          </Link>
          <p className="mt-5 max-w-md text-bone/75 leading-relaxed">
            Compassionate counseling, coaching, and LCSW supervision across Texas — one pillar at a time.
          </p>

          <form onSubmit={handleSubscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid="newsletter-email-input"
              className="flex-1 rounded-full px-5 py-3 bg-bone/10 border border-bone/20 placeholder-bone/50 text-bone focus:outline-none focus:border-terracotta transition"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="newsletter-submit-btn"
              className="rounded-full px-6 py-3 bg-terracotta hover:bg-terracottaDark transition-colors text-bone font-medium disabled:opacity-60"
            >
              {submitting ? "..." : "Subscribe"}
            </button>
          </form>
        </div>

        <div className="md:col-span-3">
          <p className="overline mb-5 text-terracotta">Explore</p>
          <ul className="space-y-3 text-bone/80">
            <li><Link to="/about" className="hover:text-terracotta transition">About</Link></li>
            <li><Link to="/services" className="hover:text-terracotta transition">Services</Link></li>
            <li><Link to="/specialties" className="hover:text-terracotta transition">Specialties</Link></li>
            <li><Link to="/shop" className="hover:text-terracotta transition">Shop</Link></li>
            <li><Link to="/blog" className="hover:text-terracotta transition">Blog</Link></li>
            <li><Link to="/contact" className="hover:text-terracotta transition">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="overline mb-5 text-terracotta">Reach Us</p>
          <ul className="space-y-4 text-bone/80">
            <li className="flex items-start gap-3">
              <MapPin size={18} className="mt-1 text-terracotta shrink-0" strokeWidth={1.5} />
              <span>Serving clients across Texas — virtual sessions available</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={18} className="mt-1 text-terracotta shrink-0" strokeWidth={1.5} />
              <a href="mailto:info@7pillarsoflife.com" className="hover:text-terracotta transition">info@7pillarsoflife.com</a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={18} className="mt-1 text-terracotta shrink-0" strokeWidth={1.5} />
              <a href="tel:8173698214" className="hover:text-terracotta transition">817-369-8214</a>
            </li>
          </ul>

          <div className="mt-7">
            <p className="overline mb-3 text-terracotta">Follow Us</p>
            <SocialIcons />
            <p className="mt-3 text-xs text-bone/55">@7pillarsoflifetherapy on Instagram · TikTok · Facebook · YouTube</p>
          </div>
        </div>
      </div>

      <div className="border-t border-bone/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-bone/60">
          <p>© {new Date().getFullYear()} Seven Pillars of Life Therapy. All rights reserved.</p>
          <p className="font-display italic text-bone/70">"One pillar at a time."</p>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-6 text-xs text-bone/55 leading-relaxed border-t border-bone/10 pt-5" data-testid="footer-bhec-notice">
          <p>
            <strong className="text-bone/75">Texas BHEC Complaint Notice:</strong>{" "}
            The Texas Behavioral Health Executive Council provides information about how to file a complaint regarding licensed behavioral health professionals in Texas.
          </p>
          <p className="mt-2 text-bone/55">
            Texas Behavioral Health Executive Council · 1801 Congress Avenue, Suite 7.300, Austin, TX 78701 · Phone: <a href="tel:18008213205" className="hover:text-terracotta">1-800-821-3205</a> · Website: <a href="https://www.bhec.texas.gov" target="_blank" rel="noopener noreferrer" className="hover:text-terracotta">https://www.bhec.texas.gov</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
